import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import * as queryString from "query-string";

import moment from "moment";
import { toast } from "react-toastify";
import { Button, Loader, Menu, Segment } from "semantic-ui-react";

import { useHistory, useLocation } from "react-router-dom";
import { useAuth0 } from "../../../../auth/auth0";
import { useModuleNavigation } from "../../../../hooks/useModuleNavigation";
import { useUserAuth } from "../../../../hooks/useUserAuth";

import * as protocolActions from "../../../../redux/actions/protocolActions";
import * as excelGenerationFunctions from "../../../../utilities/excelGeneration";

import ProtocolBoxWhisker from "./ProtocolBoxWhisker";
import ProtocolPianoCharts from "./ProtocolPianoCharts";
import ProtocolSummaryTable from "./ProtocolSummaryTable";
import ProtocolTimecourse from "./ProtocolTimecourse";

import _ from "lodash";
import { ProtocolAnalysisProvider } from "./ProtocolAnalysisContext";
import TrialMetaFactorsModal from "../../Trials/TrialMetaTags/TrialMetaFactorsModal";
import HeatmapExportModal from "../../../Lumber/HeatmapExportModal";

import "./style.css";
import { getMetadataForTrialsAsync } from "../../../../apis/trialApi";
import { generateProtocolWeatherDataExcel } from "../../../../apis/weatherDataApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { AnalysisSummary } from "../../../../apis/types/analysisTypes";
import type { ProtocolDashboardOptions, ProtocolTreatmentData } from "../../../../apis/types/protocolTypes";
import type { TrialsMetadata } from "../../../../apis/types/trialTypes";
import type { GduDataForTrials, ProtocolWeatherDataExcel } from "../../../../apis/types/weatherDataTypes";
import {
	useAnalysesQuery,
	useGduDataForTrialsQuery,
	useMeanComparisonOptionsQuery,
	useProtocolDashboardOptionsQuery,
	useProtocolDataQuery,
	useProtocolTreatmentDataQuery,
} from "./queries";
import UnsavedMetaTagsGuard from "./UnsavedMetaTagsGuard";
import type { FlattenedTrialMetadata, ToggleMetaFactorModal } from "./types";

const summaryTable = "summary-table";
const boxWhiskerChart = "box-whisker";
const timecourse = "timecourse";
const pianoChart = "piano-chart";

type AnalysisType = typeof summaryTable | typeof boxWhiskerChart | typeof timecourse | typeof pianoChart;

/** Maps the `?at=` query string to a page. Anything unrecognized shows the timecourse. */
const getAnalysisTypeFromQueryString = (qsAt: unknown): AnalysisType => {
	switch (qsAt) {
		case summaryTable:
			return summaryTable;
		case boxWhiskerChart:
			return boxWhiskerChart;
		case pianoChart:
			return pianoChart;
		default:
			return timecourse;
	}
};

/**
 * The trials metadata `select`: copies a handful of `trialInfo` fields to the top level, then sorts
 * by location, trial and cooperator.
 */
const flattenAndSortTrialMetadata = (data: TrialsMetadata): FlattenedTrialMetadata[] => {
	const res = _.map(data, (trial) => {
		// A null entry (a trial the user can't see) fails this select, leaving trialMetadataResponse undefined.
		if (!trial) {
			throw new TypeError("GetMetadataForTrials returned a null trial");
		}
		return {
			...trial,
			farmCountry: trial.trialInfo.farmCountry,
			farmCountryCode: trial.trialInfo.farmCountryCode,
			farmRegion: trial.trialInfo.farmRegion,
			farmCity: trial.trialInfo.farmCity,
			trialName: trial.trialInfo.trialName,
			trialId: trial.trialInfo.trialId,
			cooperatorName: trial.trialInfo.cooperatorName,
			hasExclusions: trial.trialInfo.hasExclusions,
			hasFlaggings: trial.trialInfo.hasFlaggings,
			hasPartialExclusions: trial.trialInfo.hasPartialExclusions,
			hasPartialFlaggings: trial.trialInfo.hasPartialFlaggings,
		};
	});

	return _.sortBy(res, ["farmCountry", "farmRegion", "farmCity", "trialName", "cooperatorName", "trialId"]);
};

/**
 * The assessment dropdown options built from the dashboard options (and the stand count analysis
 * when the trials have stand counts). Only `.length > 0` is read, to gate the summary table.
 */
const buildAssessmentOptions = (
	dashboardOptions: ProtocolDashboardOptions,
	analyses: AnalysisSummary[] | undefined,
) => {
	const groundData = _.map(
		_.uniqBy(_.sortBy(dashboardOptions.groundDataAssessments, "groundDataAssessmentName"), "groundDataAssessmentId"),
		({ groundDataAssessmentId, groundDataAssessmentName, unitName }) => ({
			key: groundDataAssessmentId,
			value: groundDataAssessmentId,
			text: groundDataAssessmentName,
			unitName: unitName,
			isgrounddata: String(true),
			iscurvemodel: String(false),
		}),
	);
	const curveModels = _.map(
		// `|| Infinity` on purpose: an order of 0 sorts last, like a missing one
		_.sortBy(dashboardOptions.curveModels, [(cma) => cma.order || Infinity, "curveModelAnalysisAbbreviation"]),
		({
			curveModelAnalysisId,
			analysisId,
			curveModelName,
			unitName,
			analysisTypeId,
			displayName,
			order,
			curveModelTimingId,
		}) => ({
			//-- React is stupid. In order to  have booleans on a DOM object, it has to be a string. All of the props have to be lower case too
			key: curveModelName,
			value: curveModelName,
			text: displayName ?? curveModelName,
			curvemodelanalysisid: curveModelAnalysisId,
			analysisid: analysisId,
			analysistypeid: analysisTypeId,
			unitName: unitName,
			isgrounddata: String(false),
			iscurvemodel: String(true),
			order: order,
			plannedTimingName: dashboardOptions.curveModelTimings.find((cmt) => cmt.id === curveModelTimingId)?.name,
		}),
	);

	//If we have stand count data then add it to the assessment selections options, otherwise don't show it as an option
	const standCountAnalysis =
		analyses && dashboardOptions.trialsContainStandCounts
			? _.find(analyses, (ao) => ao.name.includes("Stand Count (LAI)"))
			: undefined;
	if (!standCountAnalysis) {
		return [...groundData, ...curveModels];
	}

	const standCountData = {
		key: standCountAnalysis.id,
		value: standCountAnalysis.id,
		text: standCountAnalysis.name,
		analysisid: standCountAnalysis.id,
		analysistypeid: standCountAnalysis.analysisTypeId,
		isgrounddata: String(false),
		iscurvemodel: String(false),
		isStandCount: String(true),
	};
	//Move stand count to the top of the list
	return [standCountData, ...groundData, ...curveModels];
};

/** The pieces of `useUserAuth()` (an untyped JS hook) read when downloading the weather data. */
type CurrentClient = { clientName: string } | null;

const ProtocolAnalysis = () => {
	const userAuth = useUserAuth();
	const moduleNavigation = useModuleNavigation();
	const location = useLocation();
	const history = useHistory();
	const { getTokenSilently } = useAuth0();
	const dispatch = useDispatch();

	const activeItem = getAnalysisTypeFromQueryString(queryString.parse(location.search).at);

	//-- Data
	const protocolDataQuery = useProtocolDataQuery();
	const treatmentDataQuery = useProtocolTreatmentDataQuery();
	const dashboardOptionsQuery = useProtocolDashboardOptionsQuery();
	const analysesQuery = useAnalysesQuery();
	const meanComparisonOptionsQuery = useMeanComparisonOptionsQuery();

	const protocolDataResponse = protocolDataQuery.data;
	const protocolName = protocolDataResponse?.protocolData?.protocolName ?? null;
	const treatmentData = treatmentDataQuery.data;
	const dashboardOptions = dashboardOptionsQuery.data;
	const trialIds = dashboardOptions?.trialIds;

	//Undefined until the analyses request settles, [] when it failed, so pages can tell
	//"no stand count" from "not loaded yet"
	const standCountAnalyses = analysesQuery.isPending ? undefined : (analysesQuery.data?.data ?? []);

	const gduQuery = useGduDataForTrialsQuery(trialIds);
	const weatherDataDone = gduQuery.isSuccess;

	const { data: trialMetadataResponse, isFetching: metadataLoading } = useQuery({
		queryKey: ["trials metadata", trialIds, userAuth.currentClientId],
		// `enabled` guarantees both
		queryFn: async () => getMetadataForTrialsAsync(trialIds!, userAuth.currentClientId!, await getTokenSilently()),
		select: flattenAndSortTrialMetadata,
		enabled: !!trialIds && trialIds.length > 0 && !!userAuth.currentClientId,
	});

	const assessmentOptions = useMemo(
		() => (dashboardOptions ? buildAssessmentOptions(dashboardOptions, analysesQuery.data?.data) : null),
		[dashboardOptions, analysesQuery.data],
	);

	//Other protocol pages and the breadcrumbs read the protocol from redux, so keep it filled. Only
	//fresh results are written so a cached copy can't overwrite newer redux state from the edit pages.
	const protocolDataFresh = protocolDataQuery.isFetchedAfterMount;
	const treatmentDataFresh = treatmentDataQuery.isFetchedAfterMount;
	useEffect(() => {
		if (protocolDataFresh && protocolDataResponse) {
			dispatch(protocolActions.getProtocolDataSuccess(protocolDataResponse));
		}
	}, [dispatch, protocolDataFresh, protocolDataResponse]);
	useEffect(() => {
		//Treatments only land in redux once the protocol was found
		if (protocolDataFresh && protocolDataResponse?.protocolData && treatmentDataFresh && treatmentData) {
			dispatch(protocolActions.getProtocolTreatmentDataSuccess(treatmentData));
		}
	}, [dispatch, protocolDataFresh, protocolDataResponse, treatmentDataFresh, treatmentData]);

	//-- UI Control
	const loading = protocolDataQuery.isPending || dashboardOptionsQuery.isPending || treatmentDataQuery.isPending;
	const [metaFactorModalOpen, setMetaFactorsModalOpen] = useState(false);
	const [modalTrialId, setModalTrialId] = useState("");
	const [modalTrialName, setModalTrialName] = useState<string | null>("");
	const [heatmapExportModalOpen, setHeatmapExportModalOpen] = useState(false);

	const weatherDataExcelMutation = useMutation({
		mutationFn: async () => {
			const accessToken = await getTokenSilently();
			const currentDateTimeString = JSON.stringify(new Date().toLocaleString());
			const protocolNameForExcel = `${protocolName ?? "Protocol"}_ProtocolWeatherData_${moment().format("YYYYMMDD_hhmma")}`;
			//useUserAuth is untyped JS; its currentClient is a client membership once ready
			const currentClient = userAuth.currentClient as CurrentClient;
			//The endpoint answers 200 with a message string in `data` on failure
			const res = (await generateProtocolWeatherDataExcel(
				//The button is disabled until the GDU query, which needs both, has loaded
				trialIds!,
				userAuth.currentClientId!,
				currentClient?.clientName ?? "",
				currentDateTimeString,
				accessToken,
			)) as ProtocolWeatherDataExcel | string | null | undefined;
			if (!Array.isArray(res)) {
				throw new Error("GenerateProtocolWeatherDataExcel did not return any workbooks");
			}

			_.forEach(res, (trial) => {
				trial.trialName = `${trial.trialName}_${trial.cooperatorName}_TrialWeather_${moment().format(
					"YYYYMMDD_hhmma",
				)}.xlsx`;
			});
			excelGenerationFunctions.downloadZippedExcelFile(res, protocolNameForExcel);
		},
		onError: (err) => {
			console.error(err);
			toast.error("Error downloading weather data excel file. Please try again.");
		},
	});

	const toggleMetaFactorModal: ToggleMetaFactorModal = (trialId, trialName, open = true) => {
		if (trialId) {
			setModalTrialId(trialId);
			setModalTrialName(trialName);
			setMetaFactorsModalOpen(open);
		}
	};

	return loading || !userAuth.isReady ? (
		<Loader active />
	) : !protocolName ? (
		<Segment style={{ margin: 15 }}>
			<p>{"We could not find analysis data for this protocol."}</p>
		</Segment>
	) : (
		<>
			<Segment basic id="protocolAnalysis">
				<Menu pointing>
					<Menu.Item
						name="Timecourse"
						active={activeItem === timecourse}
						onClick={() => {
							history.push(`?at=${timecourse}`);
						}}
					/>
					<Menu.Item
						name="Box Whisker"
						active={activeItem === boxWhiskerChart}
						onClick={() => {
							history.push(`?at=${boxWhiskerChart}`);
						}}
					/>
					<Menu.Item
						name="Summary Table"
						active={activeItem === summaryTable}
						onClick={() => {
							history.push(`?at=${summaryTable}`);
						}}
					/>
					<Menu.Item
						name="Piano Charts"
						active={activeItem === pianoChart}
						onClick={() => {
							history.push(`?at=${pianoChart}`);
						}}
					/>
					{!!trialIds && trialIds.length > 0 && (
						<Menu.Menu position="right">
							<Menu.Item style={{ gap: "5px" }}>
								<Button
									secondary
									content="Trial Data Download"
									icon="download solid"
									labelPosition="left"
									onClick={() => setHeatmapExportModalOpen(true)}
									floated="right"
								/>
								<HeatmapExportModal
									//The route always has a protocol id
									id={moduleNavigation.protocolId!}
									open={heatmapExportModalOpen}
									setOpen={setHeatmapExportModalOpen}
									name={protocolName}
									type={"Protocol"}
								/>
								<Button
									secondary={weatherDataDone}
									floated="right"
									icon="download solid"
									labelPosition="left"
									content="Weather Data Download (.zip)"
									onClick={() => weatherDataExcelMutation.mutate()}
									loading={weatherDataExcelMutation.isPending}
									disabled={!weatherDataDone}
								/>
							</Menu.Item>
						</Menu.Menu>
					)}
				</Menu>
				<ProtocolAnalysisProvider>
					<UnsavedMetaTagsGuard trialMetadata={trialMetadataResponse} />
					<ProtocolAnalysisPagesWithContext
						activeItem={activeItem}
						dashboardOptions={dashboardOptions}
						trialMetadataResponse={trialMetadataResponse}
						metadataLoading={metadataLoading}
						hasAssessmentOptions={!!assessmentOptions && assessmentOptions.length > 0}
						hasMeanComparisonOptions={!!meanComparisonOptionsQuery.data}
						standCountAnalyses={standCountAnalyses}
						treatmentData={treatmentData}
						gduByDay={gduQuery.data ?? null}
						toggleMetaFactorModal={toggleMetaFactorModal}
					/>
				</ProtocolAnalysisProvider>
			</Segment>
			<TrialMetaFactorsModal
				open={metaFactorModalOpen}
				setOpen={setMetaFactorsModalOpen}
				trialId={modalTrialId}
				trialName={modalTrialName ?? undefined}
				isForProtocol={true}
			/>
		</>
	);
};

type ProtocolAnalysisPagesWithContextProps = {
	activeItem: AnalysisType;
	dashboardOptions: ProtocolDashboardOptions | undefined;
	trialMetadataResponse: FlattenedTrialMetadata[] | undefined;
	metadataLoading: boolean;
	/** Gates the summary table. */
	hasAssessmentOptions: boolean;
	/** Gates the timecourse: true once GetMeanComparisonsOptions has resolved. */
	hasMeanComparisonOptions: boolean;
	standCountAnalyses: AnalysisSummary[] | undefined;
	treatmentData: ProtocolTreatmentData | undefined;
	gduByDay: GduDataForTrials | null;
	toggleMetaFactorModal: ToggleMetaFactorModal;
};

/**
 * Renders the active analysis page inside ProtocolAnalysisProvider. Only rendered once the
 * protocol, treatment and dashboard option requests have settled.
 *
 * The box whisker, piano charts and summary table pages read their settings from the context and
 * render ProtocolAnalysisTopBar themselves, which builds the derived option lists from
 * dashboardOptions via ProtocolAnalysis/utils. The timecourse page is not on the context yet and
 * still takes its settings and option lists as props.
 */
const ProtocolAnalysisPagesWithContext = ({
	activeItem,
	dashboardOptions,
	trialMetadataResponse,
	metadataLoading,
	hasAssessmentOptions,
	hasMeanComparisonOptions,
	standCountAnalyses,
	treatmentData,
	gduByDay,
	toggleMetaFactorModal,
}: ProtocolAnalysisPagesWithContextProps) => {
	const hasData = !!treatmentData && !!dashboardOptions;

	return (
		<>
			{activeItem === summaryTable
				? hasData &&
					hasAssessmentOptions && (
						<ProtocolSummaryTable
							dashboardOptions={dashboardOptions}
							standCountAnalyses={standCountAnalyses}
							treatmentData={treatmentData.protocolTreatments}
							trialMetadataResponse={trialMetadataResponse}
							metadataLoading={metadataLoading}
						/>
					)
				: null}
			{activeItem === boxWhiskerChart ? (
				hasData ? (
					<ProtocolBoxWhisker
						dashboardOptions={dashboardOptions}
						standCountAnalyses={standCountAnalyses}
						treatmentData={treatmentData.protocolTreatments}
						toggleMetaFactorModal={toggleMetaFactorModal}
						trialMetadataResponse={trialMetadataResponse}
						metadataLoading={metadataLoading}
					/>
				) : (
					<Segment>
						<p>{"We could not find analysis data for this protocol."}</p>
					</Segment>
				)
			) : null}
			{activeItem === timecourse && hasData && hasMeanComparisonOptions ? (
				<ProtocolTimecourse
					dashboardOptions={dashboardOptions}
					standCountAnalyses={standCountAnalyses}
					treatmentData={treatmentData.protocolTreatments}
					trialMetadataResponse={trialMetadataResponse}
					metadataLoading={metadataLoading}
					trialAnalysisOptions={dashboardOptions.trialAnalysisOptions}
					statelessDataNormalizationOptions={dashboardOptions.dataNormalizations}
					gduByDay={gduByDay}
					toggleMetaFactorModal={toggleMetaFactorModal}
				/>
			) : null}
			{activeItem === pianoChart && hasData ? (
				<ProtocolPianoCharts dashboardOptions={dashboardOptions} treatmentData={treatmentData.protocolTreatments} />
			) : null}
		</>
	);
};

export default ProtocolAnalysis;
